import React, { createContext, useContext } from 'react';
import { useEntitlement, type CompanyEntitlement } from '@/hooks/billing/useEntitlement';

interface EntitlementContextValue {
  entitlement: CompanyEntitlement | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  enforcementState: CompanyEntitlement['enforcement_state'];
  isActive: boolean;
  isGracePeriod: boolean;
  graceDeadline: string | null;
  isRestricted: boolean;
  planName: string | null;
  isTrial: boolean;
  trialEnd: string | null;
  trialDaysRemaining: number | null;
  source: string;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

/**
 * Provider that makes subscription entitlement state available to all
 * authenticated routes. Mounted in AuthenticatedRoutesLayout alongside
 * other realtime providers.
 */
export function EntitlementProvider({ children }: { children: React.ReactNode }) {
  const entitlement = useEntitlement();

  return (
    <EntitlementContext.Provider value={entitlement}>
      {children}
    </EntitlementContext.Provider>
  );
}

/**
 * Access the entitlement context from any component within
 * AuthenticatedRoutesLayout.
 */
export function useEntitlementContext(): EntitlementContextValue {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error('useEntitlementContext must be used within EntitlementProvider');
  }
  return ctx;
}